// /opt/dashboard/frontend/src/pages/PartnerPerformance.js
// 📈 Partner-teljesítmény: kódonként megszerzett felhasználók, új regisztrációk,
// utolsó regisztráció, prémiumos userek száma + állítható "inaktív" küszöb.
import React, { useEffect, useState } from 'react';
import { PARTNER_TEMPLATES, suggestTemplate } from './partnerTemplates';
import PartnerMessageModal from '../components/PartnerMessageModal';

const C = {
  bg: '#1a1d27', panel: '#22252f', border: '#2a2e3a', text: '#e0e6ed',
  muted: '#8892a0', accent: '#00d4ff', warn: '#fbbf24', ok: '#22c55e', bad: '#ef4444',
};

const fmtDate = (ms) => {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' }); }
  catch (e) { return '—'; }
};
const daysSince = (ms) => (ms ? Math.floor((Date.now() - ms) / 86400000) : null);
const platLabel = (p) => (p === 'android' ? '🤖' : p === 'ios' ? '🍎' : '');

export default function PartnerPerformance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [threshold, setThreshold] = useState(() => {
    const v = parseInt(localStorage.getItem('perf_threshold') || '60', 10);
    return Number.isFinite(v) ? v : 60;
  });
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [sentMap, setSentMap] = useState({});

  // Kinek ment már partner-levél? Külön típus, hogy ne keveredjen a POI-outreach-csel.
  const loadSentMap = () => fetch('/api/email-send/sent-map?type=affiliate', { credentials: 'include' })
    .then(r => (r.ok ? r.json() : {})).then(setSentMap).catch(() => {});
  useEffect(() => { loadSentMap(); }, []);

  const toggle = (code) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });

  useEffect(() => {
    setLoading(true); setError(null);
    fetch('/api/affiliate-performance', { credentials: 'include' })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const setThr = (v) => {
    const n = Math.max(1, parseInt(v || '0', 10) || 0);
    setThreshold(n);
    try { localStorage.setItem('perf_threshold', String(n)); } catch (e) {}
  };

  const card = { background: C.panel, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 };
  const th = { padding: '6px 8px', textAlign: 'left', color: C.muted, borderBottom: '1px solid ' + C.border, fontWeight: 600 };
  const td = { padding: '6px 8px', borderBottom: '1px solid ' + C.border };
  const chipBtn = { padding: '6px 12px', borderRadius: 8, border: '1px solid ' + C.border,
    background: C.bg, color: C.text, fontSize: 12, cursor: 'pointer' };

  // partner állapota: inaktív, ha van már regisztrációja, de a küszöbnél régebbi az utolsó;
  // "nincs adat", ha soha nem hozott usert.
  const statusOf = (p) => {
    if (!p.total) return { label: 'nincs adat', color: C.muted };
    const d = daysSince(p.lastAttributedAt);
    if (d != null && d > threshold) return { label: `inaktív (${d} napja)`, color: C.bad };
    return { label: `aktív`, color: C.ok };
  };

  return (
    <div style={{ padding: 20, minHeight: '100vh', background: C.bg }}>
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>📈 Partner-teljesítmény</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: C.muted }}>
            <span>„Inaktív", ha</span>
            <input type="number" min="1" value={threshold} onChange={e => setThr(e.target.value)}
              style={{ width: 64, background: C.bg, border: '1px solid ' + C.border, borderRadius: 6, color: C.text, padding: '5px 8px', textAlign: 'right' }} />
            <span>napja nincs új regisztráció</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setComposeOpen(true)}
            disabled={selected.size === 0}
            style={{
              padding: '7px 14px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700,
              background: selected.size ? C.accent : C.border, color: selected.size ? '#0a0e1a' : C.muted,
              cursor: selected.size ? 'pointer' : 'default',
            }}
          >
            ✉️ Üzenet küldése ({selected.size})
          </button>
          {data && data.partners && (
            <>
              <button onClick={() => setSelected(new Set(data.partners.filter(p => p.email && !p.total).map(p => p.code)))}
                style={chipBtn}>Akik még nem indultak el</button>
              <button onClick={() => setSelected(new Set(data.partners.filter(p => p.email && p.total && daysSince(p.lastAttributedAt) > threshold).map(p => p.code)))}
                style={chipBtn}>Elhallgatott partnerek</button>
              <button onClick={() => setSelected(new Set())} style={chipBtn}>Kijelölés törlése</button>
            </>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
          Kódonként a regisztrált (attribúlt) felhasználók. A prémium megvonásához nézd az „inaktív" partnereket — a tényleges megvonást a Comped tabon teszed meg.
        </div>
      </div>

      {loading ? (
        <div style={{ color: C.muted }}>Betöltés…</div>
      ) : error ? (
        <div style={{ ...card, color: C.bad }}>
          Hiba: {error}
          <div style={{ color: C.muted, fontSize: 12, marginTop: 8 }}>
            Ha indexet kér a lekérdezés, a Firestore hibaüzenete tartalmaz egy linket az index létrehozásához.
          </div>
        </div>
      ) : (
        <div style={{ ...card }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }}></th>
                  <th style={th}>Partner</th>
                  <th style={{ ...th, textAlign: 'right' }}>Összes</th>
                  <th style={{ ...th, textAlign: 'right' }}>7n</th>
                  <th style={{ ...th, textAlign: 'right' }}>30n</th>
                  <th style={{ ...th, textAlign: 'right' }}>90n</th>
                  <th style={{ ...th, textAlign: 'right' }}>Prémium</th>
                  <th style={th}>Utolsó reg.</th>
                  <th style={th}>Állapot</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {data.partners.map(p => {
                  const st = statusOf(p);
                  const open = expanded === p.code;
                  return (
                    <React.Fragment key={p.code}>
                      <tr style={{ cursor: p.total ? 'pointer' : 'default', background: open ? 'rgba(0,212,255,0.05)' : 'transparent' }}
                        onClick={() => p.total && setExpanded(open ? null : p.code)}>
                        <td style={td} onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" disabled={!p.email}
                            title={p.email ? p.email : 'Ehhez a partnerhez nincs email cím'}
                            checked={selected.has(p.code)} onChange={() => toggle(p.code)} />
                        </td>
                        <td style={td}>
                          <span style={{ color: C.accent, fontWeight: 700, fontFamily: 'monospace' }}>{p.code}</span>
                          <span style={{ color: C.text }}> · {p.name}</span>
                          {!p.active && <span style={{ color: C.muted }}> (inaktív partner)</span>}
                          {p.email && sentMap[p.email.toLowerCase()] && (
                            <span title={'Utoljára: ' + (sentMap[p.email.toLowerCase()].lastSentAt || '?')}
                              style={{ marginLeft: 6, fontSize: 11, color: sentMap[p.email.toLowerCase()].success ? C.ok : C.bad }}>
                              ✉️{sentMap[p.email.toLowerCase()].count > 1 ? '×' + sentMap[p.email.toLowerCase()].count : ''}
                            </span>
                          )}
                          {!p.email && <span style={{ marginLeft: 6, fontSize: 11, color: C.warn }} title="Nincs email cím">⚠️</span>}
                        </td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: C.text }}>{p.total}</td>
                        <td style={{ ...td, textAlign: 'right', color: p.new7 ? C.ok : C.muted }}>{p.new7}</td>
                        <td style={{ ...td, textAlign: 'right', color: C.muted }}>{p.new30}</td>
                        <td style={{ ...td, textAlign: 'right', color: C.muted }}>{p.new90}</td>
                        <td style={{ ...td, textAlign: 'right', color: C.text }}>{p.premiumCount}</td>
                        <td style={td}>{fmtDate(p.lastAttributedAt)}</td>
                        <td style={{ ...td }}>
                          <span style={{ color: st.color, fontWeight: 600 }}>{st.label}</span>
                        </td>
                        <td style={{ ...td, textAlign: 'right', color: C.accent }}>{p.total ? (open ? '▲' : '▼') : ''}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={10} style={{ padding: 0, background: C.bg }}>
                            <div style={{ maxHeight: 320, overflowY: 'auto', padding: 8 }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                <thead>
                                  <tr>
                                    <th style={th}>Felhasználó</th>
                                    <th style={th}>Regisztrált</th>
                                    <th style={th}>Előfizetés</th>
                                    <th style={th}>Utolsó aktivitás</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {p.users.map(u => (
                                    <tr key={u.uid}>
                                      <td style={td} title={u.uid}>
                                        {platLabel(u.platform)} {u.email || u.uid.slice(0, 10) + '…'}
                                        {u.lastCity ? <span style={{ color: C.muted }}> · {u.lastCity}</span> : null}
                                      </td>
                                      <td style={td}>{fmtDate(u.attributedAt)}</td>
                                      <td style={td}>
                                        <span style={{ color: u.premium ? C.ok : C.muted }}>
                                          {u.premium ? '✓ prémium' : (u.subscriptionStatus || 'free')}
                                        </span>
                                      </td>
                                      <td style={td}>{fmtDate(u.lastActive)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {data.partners.length === 0 && (
                  <tr><td colSpan={10} style={{ ...td, color: C.muted }}>Nincs partner.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {data.orphanCodes && data.orphanCodes.length > 0 && (
            <div style={{ marginTop: 14, fontSize: 12, color: C.muted }}>
              ⚠️ Attribúció partner rekord nélkül (elgépelt/törölt kód):{' '}
              {data.orphanCodes.map(o => `${o.code} (${o.total})`).join(', ')}
            </div>
          )}
        </div>
      )}
      {composeOpen && (
        <PartnerMessageModal
          partners={(data && data.partners ? data.partners : []).filter(p => selected.has(p.code) && p.email)}
          templates={PARTNER_TEMPLATES}
          suggest={suggestTemplate}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); setSelected(new Set()); loadSentMap(); }}
        />
      )}
    </div>
  );
}
